import React, { useEffect, useRef, useState, useCallback } from "react";

import DisplayType from "utils/displayType";
import {
  getMember,
  getMemberPortfolios,
  ApiMemberPortfolioDetail,
} from "lib/latticeApi";
import styled from "@emotion/styled";
import PortfolioCard from "component/portfolio-card/PortfolioCard";
import { Pagination } from "@mui/material";
import DefaultButton from "admin/component/button/DefaultButton";
import ModalLayout from "admin/component/modal/ModalLayout";
import PortfolioModal from "router/portfolio/organism/PortfolioModal";
import { useLanguage } from "i18n/LanguageContext";

const DESKTOP_LIMIT = 6;
const MOBILE_LIMIT = 4;

interface Props {
  memberId: string;
}

const DetailInvests: React.FC<Props> = ({ memberId }) => {
  const mode = DisplayType();
  const { t } = useLanguage();

  const selectedPortfolioId = useRef<string>("");
  const [portfolioModalOpen, updatePortfolioModalOpen] =
    useState<boolean>(false);

  const limit = mode === "Mobile" ? MOBILE_LIMIT : DESKTOP_LIMIT;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [portfolioList, setPortfolioList] = useState<
    ApiMemberPortfolioDetail[]
  >([]);
  const [mobileAccumulated, setMobileAccumulated] = useState<
    ApiMemberPortfolioDetail[]
  >([]);
  const onClickPortfolio = (id: string) => {
    selectedPortfolioId.current = id;
    updatePortfolioModalOpen(true);
  };

  const fetchPortfolios = useCallback(async () => {
    try {
      const { portfolios, pagination } = await getMemberPortfolios(
        memberId,
        currentPage,
        limit
      );
      setPortfolioList(portfolios);
      setTotalPage(pagination.total_pages);

      if (mode === "Mobile" && currentPage > 1) {
        setMobileAccumulated((prev) => [...prev, ...portfolios]);
      } else {
        setMobileAccumulated(portfolios);
      }
    } catch {
      // API 미배포 시 getMember 폴백
      try {
        const member = await getMember(memberId);
        const all = member.portfolios || [];
        const start = (currentPage - 1) * limit;
        const sliced = all.slice(start, start + limit);
        setPortfolioList(sliced);
        setTotalPage(Math.ceil(all.length / limit));

        if (mode === "Mobile" && currentPage > 1) {
          setMobileAccumulated((prev) => [...prev, ...sliced]);
        } else {
          setMobileAccumulated(all.slice(0, currentPage * limit));
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [memberId, currentPage, limit, mode]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const STAGE_LABEL: Record<string, string> = {
    seed: "Seed",
    venture_growth: "Venture & Growth",
    alumni: "Alumni",
  };

  const enrichWithStage = (list: ApiMemberPortfolioDetail[]) =>
    list.map((item) => ({
      ...item,
      stage: STAGE_LABEL[item.stage || ""] || item.stage || "",
    }));

  const displayList = enrichWithStage(
    mode === "Mobile" ? mobileAccumulated : portfolioList
  );

  if (displayList.length === 0 && currentPage === 1) return null;

  return (
    <Container>
      <LabelContainer>
        <span className="font-sub-title2-en">Investments</span>
      </LabelContainer>
      <PortfolioList>
        {displayList.map((item) => {
          return (
            <PortfolioCard
              key={item.id}
              portfolio={item}
              isViewer
              onClick={() => {
                onClickPortfolio(item.id);
              }}
            />
          );
        })}
      </PortfolioList>
      {mode !== "Mobile" && totalPage > 1 && (
        <div className="center" style={{ marginTop: 16 }}>
          <Pagination
            count={totalPage}
            shape="rounded"
            page={currentPage}
            onChange={(event, page) => {
              setCurrentPage(page);
            }}
          />
        </div>
      )}
      {mode === "Mobile" && totalPage > 0 && currentPage < totalPage && (
        <div className="center" style={{ marginTop: 40 }}>
          <DefaultButton
            className="font-button2"
            type="outline"
            onClick={() => {
              setCurrentPage(currentPage + 1);
            }}
          >
            {t("portfolio", "morePortfolioCompany")}
          </DefaultButton>
        </div>
      )}

      <ModalLayout isOpen={portfolioModalOpen} borderRadius={0}>
        <PortfolioModal
          portfolioId={selectedPortfolioId.current}
          close={() => {
            selectedPortfolioId.current = "";
            updatePortfolioModalOpen(false);
          }}
        />
      </ModalLayout>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 100px;

  .more__btn {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 64px;
  }

  .center {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  text-transform: uppercase;
`;

const PortfolioList = styled.ul`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 24px;
  padding: 0;
  margin-top: 24px;

  @media only screen and (max-width: 768px) {
    column-gap: 7px;
    row-gap: 24px;
    margin-top: 16px;
  }
`;
export default React.memo(DetailInvests);
